import "@/App.css";
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/api";

import Welcome from "@/pages/Welcome";
import EmailAuth from "@/pages/EmailAuth";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import JobFiles from "@/pages/JobFiles";
import Documents from "@/pages/Documents";
import Folders from "@/pages/Folders";
import ClientDetail from "@/pages/ClientDetail";
import Kai from "@/pages/Kai";
import Account from "@/pages/Account";
import DocumentEditor from "@/pages/DocumentEditor";
import DocumentDetail from "@/pages/DocumentDetail";
import Legal from "@/pages/Legal";
import CodeGate from "@/pages/CodeGate";
import Paywall from "@/pages/Paywall";
import Admin from "@/pages/Admin";

const Splash = () => (
  <div className="min-h-screen flex items-center justify-center bg-surface">
    <div className="w-12 h-12 rounded-[12px] bg-brandPrimary animate-pulse" />
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        localStorage.setItem("docket_token", data.token);
        setAuth(data.user, data.access);
        window.history.replaceState(null, "", "/");
        navigate(data.user.onboarded ? "/home" : "/onboarding", { replace: true });
      } catch (e) {
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setAuth]);
  return <Splash />;
}

function Protected({ children, requireOnboarded = true, requireUnlocked = true }) {
  const { user, access, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  if (requireOnboarded && !user.onboarded) return <Navigate to="/onboarding" replace />;
  if (requireUnlocked && user.onboarded && access && !access.unlocked) return <Navigate to={access.locked ? "/unlock" : "/paywall"} replace />;
  return children;
}

function Gate() {
  const { user, access, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Welcome />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (access && !access.unlocked) return <Navigate to={access.locked ? "/unlock" : "/paywall"} replace />;
  return <Navigate to="/home" replace />;
}

function AuthGate() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return <EmailAuth />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Gate />} />
      <Route path="/auth" element={<AuthGate />} />
      <Route path="/onboarding" element={<Protected requireOnboarded={false} requireUnlocked={false}><Onboarding /></Protected>} />
      <Route path="/unlock" element={<Protected requireUnlocked={false}><CodeGate /></Protected>} />
      <Route path="/home" element={<Protected><Home /></Protected>} />
      <Route path="/jobs" element={<Navigate to="/documents" replace />} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/folders" element={<Protected><Folders /></Protected>} />
      <Route path="/jobs/:id" element={<Protected><ClientDetail /></Protected>} />
      <Route path="/kai" element={<Protected><Kai /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/new/:type" element={<Protected><DocumentEditor /></Protected>} />
      <Route path="/edit/:id" element={<Protected><DocumentEditor /></Protected>} />
      <Route path="/doc/:id" element={<Protected><DocumentDetail /></Protected>} />
      <Route path="/paywall" element={<Protected requireUnlocked={false}><Paywall /></Protected>} />
      <Route path="/legal/:page" element={<Legal />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
